import { Injectable, signal, computed } from '@angular/core';

// Para usar este servicio, basta con ponerlo en el constructor

// constructor(public systemContext: SystemContextService) {}

// Y luego encerramos lo que queramos con ngIf como se ve aca abajo de ejemplo:

// <div *ngIf="systemContext.is('hse')">
// <app-tarjeta-hse></app-tarjeta-hse>
// </div>

export type System = 'hse' | 'rrhh' | 'lab' | 'administracion-blister' | null;
export interface SystemContextState {
    system: System;
    moduleId: string | null;
    submoduleId: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class SystemContextService {
    private readonly storageKeyV2 = 'systemContextV2';
    private contextSignal = signal<SystemContextState>({ system: null, moduleId: null, submoduleId: null });

    constructor() {
        const rawV2 = localStorage.getItem(this.storageKeyV2);
        if (rawV2) {
            try {
                const parsed = JSON.parse(rawV2) as Partial<SystemContextState>;
                if (parsed) {
                    this.contextSignal.set({
                        system: (parsed.system ?? null) as System,
                        moduleId: parsed.moduleId ?? null,
                        submoduleId: parsed.submoduleId ?? null,
                    });
                }
            } catch {
                /* noop */
            }
        } else {
            // Compatibilidad con el valor previo (solo sistema en string)
            const legacy = localStorage.getItem('systemContext') as System;
            if (legacy === 'hse' || legacy === 'rrhh' || legacy === 'lab' || legacy === 'administracion-blister') {
                this.contextSignal.set({ system: legacy, moduleId: null, submoduleId: null });
            }
        }
    }

    private persist(ctx: SystemContextState) {
        localStorage.setItem(this.storageKeyV2, JSON.stringify(ctx));
        if (ctx.system) {
            localStorage.setItem('systemContext', ctx.system);
        } else {
            localStorage.removeItem('systemContext');
        }
    }

    private setContext(partial: Partial<SystemContextState>) {
        this.contextSignal.update((prev) => {
            const next: SystemContextState = { ...prev, ...partial };
            if (!next.system) {
                next.moduleId = null;
                next.submoduleId = null;
            }
            this.persist(next);
            return next;
        });
    }

    setSystem(system: System, moduleId: string | null = null) {
        this.setContext({ system, moduleId, submoduleId: null });
    }

    setModule(moduleId: string | null) {
        this.setContext({ moduleId, submoduleId: null });
    }

    setSubmodule(submoduleId: string | null) {
        this.setContext({ submoduleId });
    }

    getSystem(): System {
        return this.contextSignal().system;
    }

    getModuleId(): string | null {
        return this.contextSignal().moduleId;
    }

    getSubmoduleId(): string | null {
        return this.contextSignal().submoduleId;
    }

    system$ = computed(() => this.contextSignal().system);

    is(system: 'hse' | 'rrhh' | 'lab' | 'administracion-blister'): boolean {
        return this.contextSignal().system === system;
    }

    clear() {
        this.setSystem(null);
    }
}
